// src/utils/datetime.js
// ─────────────────────────────────────────────────────────────────────────────
// Convenção única de timezone do BLB System
//
//   • O banco guarda SEMPRE horário UTC (wall-clock UTC em colunas DATETIME).
//   • O backend compara SEMPRE em UTC (UTC_TIMESTAMP() no MySQL).
//   • O frontend faz o parse como UTC e só na exibição converte para SP.
//
// Isso elimina o bug de "+3h": uma string "2026-02-01 18:00:00" vinda do banco
// é UTC; se o browser fizer new Date("2026-02-01T18:00:00") ela é interpretada
// como horário LOCAL (SP, UTC-3) e o instante "anda" 3h. Aqui forçamos o 'Z'.
//
// São José dos Campos/SP = America/Sao_Paulo = UTC-3 fixo (sem horário de verão
// desde 2019). Onde precisamos converter um horário de parede de SP para UTC,
// usamos o offset fixo -03:00 — robusto independentemente do fuso do browser do
// admin que estiver editando.
// ─────────────────────────────────────────────────────────────────────────────

export const APP_TZ = 'America/Sao_Paulo';
export const SP_OFFSET = '-03:00'; // UTC-3 fixo

/**
 * Faz o parse de um valor vindo da API como um INSTANTE absoluto.
 *  - number            → epoch ms
 *  - Date              → como está
 *  - ISO com tz (Z/±hh)→ respeitado
 *  - string "naive"    → tratada como UTC (nossa convenção de banco)
 * Retorna Date ou null.
 */
export function parseUtc(value) {
  if (value === null || value === undefined || value === '') return null;
  if (value instanceof Date) return isNaN(value.getTime()) ? null : value;
  if (typeof value === 'number') {
    const d = new Date(value);
    return isNaN(d.getTime()) ? null : d;
  }
  let s = String(value).trim();
  if (!s) return null;
  const hasTz = /([zZ])|([+-]\d{2}:?\d{2})$/.test(s);
  s = s.replace(' ', 'T');
  if (!hasTz) s += 'Z'; // naive = UTC
  const d = new Date(s);
  return isNaN(d.getTime()) ? null : d;
}

/** Formata um instante para "dd/mm/aaaa hh:mm" no fuso de SP. */
export function formatDateTimeSP(value, opts = {}) {
  const d = parseUtc(value);
  if (!d) return '—';
  return new Intl.DateTimeFormat('pt-BR', {
    timeZone: APP_TZ,
    day: '2-digit', month: '2-digit', year: 'numeric',
    hour: '2-digit', minute: '2-digit',
    ...opts,
  }).format(d);
}

/** Formata só a data "dd/mm/aaaa" em SP. */
export function formatDateSP(value) {
  return formatDateTimeSP(value, { hour: undefined, minute: undefined });
}

/** Texto relativo curto ("há 3h", "em 2d"), base em SP-agnóstico (é duração). */
export function fromNowSP(value) {
  const d = parseUtc(value);
  if (!d) return '—';
  const diff = d.getTime() - Date.now(); // >0 futuro, <0 passado
  const past = diff < 0;
  const abs = Math.abs(diff);
  const min = Math.floor(abs / 60000);
  const h = Math.floor(min / 60);
  const day = Math.floor(h / 24);
  const label =
    day > 0 ? `${day}d` : h > 0 ? `${h}h` : min > 0 ? `${min}min` : 'agora';
  if (label === 'agora') return 'agora';
  return past ? `há ${label}` : `em ${label}`;
}

/**
 * Converte o valor de um <input type="datetime-local"> — que representa horário
 * de PAREDE de SP ("YYYY-MM-DDTHH:MM") — para ISO UTC pra enviar à API.
 * Usa offset fixo -03:00 (SP sem DST), então não depende do fuso do browser.
 */
export function spInputToUtcIso(localValue) {
  if (!localValue) return null;
  const withSeconds = localValue.length === 16 ? `${localValue}:00` : localValue;
  const d = new Date(`${withSeconds}${SP_OFFSET}`);
  return isNaN(d.getTime()) ? null : d.toISOString();
}

/**
 * Converte um instante UTC para o valor de um <input type="datetime-local">
 * expresso em horário de parede de SP ("YYYY-MM-DDTHH:MM").
 */
export function utcToSpInput(value) {
  const d = parseUtc(value);
  if (!d) return '';
  const p = new Intl.DateTimeFormat('en-CA', {
    timeZone: APP_TZ,
    year: 'numeric', month: '2-digit', day: '2-digit',
    hour: '2-digit', minute: '2-digit', hour12: false,
  })
    .formatToParts(d)
    .reduce((acc, part) => { acc[part.type] = part.value; return acc; }, {});
  return `${p.year}-${p.month}-${p.day}T${p.hour}:${p.minute}`;
}

/**
 * Decompõe a contagem regressiva de agora até `target` (instante UTC).
 * Retorna { expired, ms, days, hours, minutes, seconds, totalSec } ou null.
 */
export function countdownTo(target) {
  const d = parseUtc(target);
  if (!d) return null;
  let ms = d.getTime() - Date.now();
  const expired = ms <= 0;
  if (ms < 0) ms = 0;
  const totalSec = Math.floor(ms / 1000);
  return {
    expired,
    ms,
    totalSec,
    days: Math.floor(totalSec / 86400),
    hours: Math.floor((totalSec % 86400) / 3600),
    minutes: Math.floor((totalSec % 3600) / 60),
    seconds: totalSec % 60,
  };
}